import { Measurement } from '@cosmos/shared';
import { OverlayConfig } from '../../data/charts/chart-options/overlay-axis';
import { CatalogType } from './compare-catalog';
import {
  CompareFilterState, CompareSeries, CompareTranslate, CompareTypeAdapter, CompareVariable,
} from './registry/compare-type-adapter.model';

/** Shape fed to cosmos-multi-line-chart: `label` is the verbatim legend text, `[variable.key]` holds the values. */
export interface CompareDataObject {
  label: string;
  time: Date[];
  meta?: CompareSeries['meta'];
  [variableKey: string]: any;
}

export interface CompareChartModel {
  chartKey: string;                                   // `${type}:${variable.key}`
  type: string;
  variable: CompareVariable;
  dataObjects: CompareDataObject[];
  /** Every other variable of the same type — candidates for the right-axis overlay. */
  overlayOptions: { key: string; label: string }[];
  overlay: OverlayConfig | null;
}
export interface CompareLayoutSection { section: string; sectionLabelKey: string; charts: CompareChartModel[] }
export interface CompareLayoutType { type: string; sections: CompareLayoutSection[] }

export function buildChartDataObjects(
  adapter: CompareTypeAdapter,
  measurements: Measurement[],
  variable: CompareVariable,
  filterState: Record<string, CompareFilterState>,
  t: CompareTranslate,
): CompareDataObject[] {
  return measurements.flatMap((m) =>
    adapter.buildSeries(m, variable, filterState[m._id as string] ?? {}, t).map((s) => ({
      label: s.label,
      time: s.time,
      meta: s.meta,
      [variable.key]: s.values,
    })),
  );
}

/**
 * type → section → chart layout for the selected variables. Charts/sections/types
 * with no data are dropped. `selected` entries are `${type}:${variableKey}`.
 */
export function buildCompareLayout(
  catalog: CatalogType[],
  selected: ReadonlySet<string>,
  measurements: Measurement[],
  registry: Record<string, CompareTypeAdapter>,
  filterState: Record<string, CompareFilterState>,
  t: CompareTranslate,
  /** chartKey → overlay variable key. */
  overlays: Record<string, string | null> = {},
): CompareLayoutType[] {
  const out: CompareLayoutType[] = [];
  for (const typeBlock of catalog) {
    const adapter = registry[typeBlock.type];
    if (!typeBlock.supported || !adapter) continue;
    const ofType = measurements.filter((m) => m.measurementType === typeBlock.type);

    const sections: CompareLayoutSection[] = [];
    for (const sec of typeBlock.sections) {
      const charts: CompareChartModel[] = [];
      for (const v of sec.variables) {
        const chartKey = `${typeBlock.type}:${v.key}`;
        if (!selected.has(chartKey)) continue;
        const dataObjects = buildChartDataObjects(adapter, ofType, v, filterState, t);
        if (!dataObjects.length) continue;
        const allVars = typeBlock.sections.flatMap((s) => s.variables);
        const overlayVar = allVars.find((o) => o.key === overlays[chartKey] && o.key !== v.key);
        let overlay: OverlayConfig | null = null;
        if (overlayVar) {
          const overlayObjects = buildChartDataObjects(adapter, ofType, overlayVar, filterState, t);
          if (overlayObjects.length) {
            overlay = {
              dataObjects: overlayObjects, dataset: overlayVar.key, label: t(overlayVar.labelKey), unit: overlayVar.unit,
              primaryLabel: t(v.labelKey), primaryUnit: v.unit,
            };
          }
        }
        charts.push({
          chartKey, type: typeBlock.type, variable: v, dataObjects, overlay,
          overlayOptions: allVars.filter((o) => o.key !== v.key).map((o) => ({ key: o.key, label: t(o.labelKey) })),
        });
      }
      if (charts.length) sections.push({ section: sec.section, sectionLabelKey: sec.sectionLabelKey, charts });
    }
    if (sections.length) out.push({ type: typeBlock.type, sections });
  }
  return out;
}
